import React, { useState, useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Password } from 'primereact/password';
import { useLocation, useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Shield, Edit, Save, AlertCircle } from 'lucide-react';
import { getRequest } from '../services/apiService';
import { motion } from 'framer-motion';

const UserProfile = () => {
  const toast = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [animateProfile, setAnimateProfile] = useState(false);

  // For changing password
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Profile details
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    loginname: '',
    user_role: {}, 
    hrcdf_designation: 'N/A',
    rankCode: 'N/A',
    rankName: 'N/A',
    unit: 'Not Assigned'
  });

  // Fetch profile data from the API
  const fetchProfile = async () => {
    setIsLoading(true);
    try {
      const response = await getRequest('/api/auth/users/1/');
      if (response.status === 'success' && response.data) {
        setProfileData({
          first_name: response.data.first_name || '',
          last_name: response.data.last_name || '',
          email: response.data.email || '',
          loginname: response.data.loginname || '',
          user_role: response.data.user_role || {},
          hrcdf_designation: response.data.hrcdf_designation || 'N/A',
          rankCode: response.data.rankCode || 'N/A',
          rankName: response.data.rankName || 'N/A',
          unit: response.data.unit ? response.data.unit : 'Not Assigned'
        });
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load profile.',
        life: 3000
      });
    } finally {
      setIsLoading(false);
      setTimeout(() => setAnimateProfile(true), 300);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  // URL parameters handling for tab navigation
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    
    if (tab === 'changePassword') {
      setActiveTab(1);
    } else {
      setActiveTab(0);
    }
  }, [location]);

  const navigateToTab = (tabIndex) => {
    setAnimateProfile(false);
    setTimeout(() => {
      setActiveTab(tabIndex);
      const params = new URLSearchParams();
      if (tabIndex === 1) {
        params.set('tab', 'changePassword');
      } else {
        params.delete('tab');
      }
      navigate(`/profile${params.toString() ? `?${params.toString()}` : ''}`, { replace: true });
      setTimeout(() => setAnimateProfile(true), 300);
    }, 300);
  };

  // Handle password field changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordSubmit = () => {
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'New passwords do not match',
        life: 3000
      });
      return;
    }
    
    // Animation for success
    const passwordFormEl = document.getElementById('password-form');
    passwordFormEl.classList.add('success-animation');
    
    setTimeout(() => {
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Password updated successfully',
        life: 3000
      });
      setPasswords({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      passwordFormEl.classList.remove('success-animation');
    }, 800);
  };

  // Profile Card Item component
  const ProfileInfoItem = ({ label, value, icon }) => (
    <motion.div 
      className="flex flex-col space-y-2 bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md hover:border-indigo-100 transition-all"
      whileHover={{ y: -5, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
      initial={{ opacity: 0, y: 20 }}
      animate={animateProfile ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
        {icon}
        {label}
      </div>
      <div className="font-medium text-gray-800 text-lg">{value}</div>
    </motion.div>
  );

  const getInitials = () => {
    const firstName = profileData.first_name || '';
    const lastName = profileData.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toast ref={toast} />
      
      {/* Profile Header */}
      <motion.div 
        className="bg-gradient-to-r from-indigo-800 to-indigo-700 relative overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="absolute inset-0 bg-pattern opacity-10"></div>
        <motion.div 
          className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-12 relative z-10"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="flex items-center gap-6">
              <motion.div 
                className="h-24 w-24 rounded-full bg-white shadow-lg flex items-center justify-center text-indigo-800 text-3xl font-bold ring-4 ring-white/20"
                whileHover={{ scale: 1.05, boxShadow: '0 0 0 4px rgba(255,255,255,0.4)' }}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.5 }}
              >
                {isLoading ? '...' : getInitials()}
              </motion.div>
              <div>
                <motion.h1 
                  className="text-2xl md:text-3xl font-bold text-white"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                >
                  {isLoading ? 'Loading...' : `${profileData.first_name} ${profileData.last_name}`}
                </motion.h1>
                <motion.div 
                  className="flex flex-wrap items-center mt-2 gap-2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7, duration: 0.5 }}
                >
                  <span className="bg-white/20 text-white text-sm px-3 py-1 rounded-full flex items-center gap-1">
                    <User className="h-3.5 w-3.5" />
                    {isLoading ? '...' : profileData.loginname}
                  </span>
                  <span className="bg-white/20 text-white text-sm px-3 py-1 rounded-full flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" />
                    {isLoading ? '...' : profileData.email}
                  </span>
                  <span className="bg-white/20 text-white text-sm px-3 py-1 rounded-full flex items-center gap-1">
                    <Shield className="h-3.5 w-3.5" />
                    {isLoading ? '...' : (profileData.user_role && profileData.user_role.name) || 'User'}
                  </span>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Tab Navigation */}
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <motion.div 
          className="flex mb-6 bg-white rounded-xl shadow-sm overflow-hidden"
          variants={fadeIn}
          initial="hidden"
          animate="visible"
        >
          <button
            onClick={() => navigateToTab(0)}
            className={`flex-1 py-4 px-2 text-center font-medium flex items-center justify-center gap-2 ${
              activeTab === 0 
                ? 'text-indigo-800 border-b-2 border-indigo-800 bg-indigo-50' 
                : 'text-gray-600 hover:bg-gray-50'
            } transition-all duration-200`}
          >
            <User className={`h-5 w-5 ${activeTab === 0 ? 'text-indigo-800' : 'text-gray-500'}`} />
            <span className="text-sm md:text-base">Profile Information</span>
          </button>
          <button
            onClick={() => navigateToTab(1)}
            className={`flex-1 py-4 px-2 text-center font-medium flex items-center justify-center gap-2 ${
              activeTab === 1 
                ? 'text-indigo-800 border-b-2 border-indigo-800 bg-indigo-50' 
                : 'text-gray-600 hover:bg-gray-50'
            } transition-all duration-200`}
          >
            <Lock className={`h-5 w-5 ${activeTab === 1 ? 'text-indigo-800' : 'text-gray-500'}`} />
            <span className="text-sm md:text-base">Security Settings</span>
          </button>
        </motion.div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Profile Tab */}
          {activeTab === 0 && (
            <motion.div 
              className="p-5 md:p-8"
              initial={{ opacity: 0 }}
              animate={animateProfile ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={animateProfile ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <User className="h-5 w-5 text-indigo-800" />
                    Personal Information
                  </h2>
                  <Button
                    label="Refresh Profile"
                    icon="pi pi-refresh"
                    className="bg-indigo-50 text-indigo-800 border-none px-4 py-2 hover:bg-indigo-100"
                    onClick={fetchProfile}
                  />
                </div>
              </motion.div>

              {isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-800"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 md:gap-6">
                  <ProfileInfoItem 
                    label="First Name" 
                    value={profileData.first_name} 
                    icon={<User className="h-4 w-4 text-indigo-800" />} 
                  />
                  <ProfileInfoItem 
                    label="Last Name" 
                    value={profileData.last_name} 
                    icon={<User className="h-4 w-4 text-indigo-800" />} 
                  />
                  <ProfileInfoItem 
                    label="Login Name" 
                    value={profileData.loginname} 
                    icon={<Shield className="h-4 w-4 text-indigo-800" />} 
                  />
                  <ProfileInfoItem 
                    label="Email" 
                    value={profileData.email} 
                    icon={<Mail className="h-4 w-4 text-indigo-800" />} 
                  />
                  <ProfileInfoItem 
                    label="Designation" 
                    value={profileData.hrcdf_designation || (profileData.user_role && profileData.user_role.name) || 'N/A'} 
                    icon={<Shield className="h-4 w-4 text-indigo-800" />} 
                  />
                  <ProfileInfoItem 
                    label="Rank Code" 
                    value={profileData.rankCode || 'N/A'} 
                    icon={<Shield className="h-4 w-4 text-indigo-800" />} 
                  />
                  <ProfileInfoItem 
                    label="Rank Name" 
                    value={profileData.rankName || 'N/A'} 
                    icon={<Shield className="h-4 w-4 text-indigo-800" />} 
                  />
                  <ProfileInfoItem 
                    label="Unit" 
                    value={profileData.unit || 'Not Assigned'} 
                    icon={<Shield className="h-4 w-4 text-indigo-800" />} 
                  />
                </div>
              )}
            </motion.div>
          )}

          {/* Password Tab */}
          {activeTab === 1 && (
            <motion.div 
              className="p-5"
              initial={{ opacity: 0 }}
              animate={animateProfile ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="max-w-md mx-auto" id="password-form">
                <motion.div 
                  className="bg-gradient-to-r from-indigo-800 to-indigo-700 px-6 py-8 rounded-t-2xl"
                  initial={{ y: -20, opacity: 0 }}
                  animate={animateProfile ? { y: 0, opacity: 1 } : { y: -20, opacity: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                >
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-white/20 p-3 rounded-full">
                      <Lock className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold text-white text-center">Security Center</h2>
                  <p className="text-sm text-white/80 text-center mt-2">
                    Protect your account with a strong, unique password
                  </p>
                </motion.div>
                
                <motion.div 
                  className="bg-white px-8 py-8 rounded-b-2xl shadow-xl border border-gray-100"
                  initial={{ y: 20, opacity: 0 }}
                  animate={animateProfile ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  <div className="space-y-6">
                    <motion.div 
                      initial={{ x: -10, opacity: 0 }}
                      animate={animateProfile ? { x: 0, opacity: 1 } : { x: -10, opacity: 0 }}
                      transition={{ duration: 0.3, delay: 0.3 }}
                    >
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Current Password
                      </label>
                      <div className="relative">
                        <Password
                          className="w-full"
                          inputClassName="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-300 focus:border-indigo-800 focus:ring focus:ring-indigo-200 transition duration-200 shadow-sm"
                          value={passwords.currentPassword}
                          onChange={handlePasswordChange}
                          name="currentPassword"
                          feedback={false}
                          toggleMask
                        />
                        <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-500">
                          <Lock className="h-4 w-4" />
                        </span>
                      </div>
                    </motion.div>
                    
                    <motion.div
                      initial={{ x: -10, opacity: 0 }}
                      animate={animateProfile ? { x: 0, opacity: 1 } : { x: -10, opacity: 0 }}
                      transition={{ duration: 0.3, delay: 0.4 }}
                    >
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        New Password
                      </label>
                      <div className="relative">
                        <Password
                          className="w-full"
                          inputClassName="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-300 focus:border-indigo-800 focus:ring focus:ring-indigo-200 transition duration-200 shadow-sm"
                          value={passwords.newPassword}
                          onChange={handlePasswordChange}
                          name="newPassword"
                          promptLabel="Choose a password"
                          weakLabel="Too simple"
                          mediumLabel="Average complexity"
                          strongLabel="Complex password"
                          toggleMask
                        />
                        <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-500">
                          <Lock className="h-4 w-4" />
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-xs text-amber-700 bg-amber-50 p-2 rounded-lg">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <p>Must be at least 8 characters with numbers and special characters.</p>
                      </div>
                    </motion.div>
                    
                    <motion.div
                      initial={{ x: -10, opacity: 0 }}
                      animate={animateProfile ? { x: 0, opacity: 1 } : { x: -10, opacity: 0 }}
                      transition={{ duration: 0.3, delay: 0.5 }}
                    >
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <Password
                          className="w-full"
                          inputClassName="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-300 focus:border-indigo-800 focus:ring focus:ring-indigo-200 transition duration-200 shadow-sm"
                          value={passwords.confirmPassword}
                          onChange={handlePasswordChange}
                          name="confirmPassword"
                          feedback={false}
                          toggleMask
                        />
                        <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-500">
                          <Lock className="h-4 w-4" />
                        </span>
                      </div>
                    </motion.div>
                    
                    <motion.div
                      initial={{ y: 10, opacity: 0 }}
                      animate={animateProfile ? { y: 0, opacity: 1 } : { y: 10, opacity: 0 }}
                      transition={{ duration: 0.3, delay: 0.6 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Button
                        label="Update Password"
                        // icon="pi pi-lock"
                        onClick={handlePasswordSubmit}
                        className="w-full bg-indigo-800 hover:bg-indigo-700 text-white py-3 rounded-xl font-semibold shadow-md transition duration-200 flex items-center justify-center gap-2 "
                      />
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <style jsx>{`
        .bg-pattern {
          background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E");
        }
        
        @keyframes success-pulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(55, 48, 163, 0);
          }
          50% {
            box-shadow: 0 0 0 15px rgba(55, 48, 163, 0.2);
          }
        }
        
        .success-animation {
          animation: success-pulse 1.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default UserProfile;